import type { StateCreator, StoreMutatorIdentifier } from "zustand";

import { VidyoCommonState } from "@/features/vidyo-call/types/vidyo-common";
import * as Sentry from "@sentry/react";

type Context = Record<string, unknown>;

interface SentryMiddlewareConfig {
  /**
   * A function that takes the current state as parameter and return the state that will be stored on Sentry.
   * Return null to not attach the state
   * @link https://docs.sentry.io/platforms/javascript/guides/react/features/redux/#redux-enhancer-options
   */
  stateTransformer?: (state: VidyoCommonState) => Context | null;
}

type SentryMiddleware = <
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  fn: StateCreator<VidyoCommonState, Mps, Mcs>,
  config?: SentryMiddlewareConfig,
) => StateCreator<VidyoCommonState, Mps, Mcs>;

type SentryMiddlewareImpl = (
  fn: StateCreator<VidyoCommonState>,
  config?: SentryMiddlewareConfig,
) => StateCreator<VidyoCommonState>;

/**
 * A Sentry middleware for zustand that will store the latest state in Sentry's context.
 */
const baseSentryMiddleware: SentryMiddlewareImpl =
  (fn, sentryConfig) => (set, get, store) => {
    // Add set proxy
    const sentrySet: typeof set = (...a) => {
      set(...(a as Parameters<typeof set>));
      setSentryContext(get());
    };

    // Add setState proxy
    const setState = store.setState;

    store.setState = (...a) => {
      setState(...(a as Parameters<typeof setState>));
      setSentryContext(store.getState());
    };

    return fn(sentrySet, get, store);

    function setSentryContext(state: VidyoCommonState): void {
      const currentScope = Sentry.getCurrentScope();

      const transformedState = sentryConfig?.stateTransformer
        ? sentryConfig.stateTransformer(state)
        : state;

      if (transformedState !== null) {
        const context = transformedState as Context;
        currentScope.setContext("zustand-state", context);
        currentScope.setTag("room-key", state.roomKey);
      } else {
        // Same as https://github.com/getsentry/sentry-javascript/blob/8.37.1/packages/react/src/redux.ts#L149
        currentScope.setContext("zustand-state", null);
        currentScope.setTag("room-key", null);
      }
    }
  };

/**
 * A Sentry middleware for zustand that will store the latest state in Sentry's context.
 */
const sentryMiddleware = baseSentryMiddleware as unknown as SentryMiddleware;

export default sentryMiddleware;
